// lib/list-generation-aspect-ratio.ts
import type { NanoBananaAspectRatio, FluxTwoProAspectRatio } from '#types/types';

export const NANO_SUPPORTED_RATIOS: Record<Exclude<NanoBananaAspectRatio, 'auto'>, number> = {
  '1:1': 1, '2:3': 2 / 3, '3:2': 3 / 2, '3:4': 3 / 4, '4:3': 4 / 3,
  '4:5': 4 / 5, '5:4': 5 / 4, '9:16': 9 / 16, '16:9': 16 / 9, '21:9': 21 / 9,
};
export const NANO_2_SUPPORTED_RATIOS: Record<string, number> = {
  ...NANO_SUPPORTED_RATIOS, '1:4': 1 / 4, '4:1': 4, '1:8': 1 / 8, '8:1': 8,
};
export const FLUX_TWO_SUPPORTED_RATIOS: Record<Exclude<FluxTwoProAspectRatio, 'match_input_image'>, number> = {
  '1:1': 1, '16:9': 16 / 9, '3:2': 3 / 2, '2:3': 2 / 3, '4:5': 4 / 5,
  '5:4': 5 / 4, '9:16': 9 / 16, '3:4': 3 / 4, '4:3': 4 / 3,
};

export interface ResolveNanoListAspectRatioInput {
  explicitAspectRatio?: unknown;
  sourceWidth?: number;
  sourceHeight?: number;
  workspaceAspectRatio?: number;
  rectAspectRatio: number;
  coversEntireFrame: boolean;
  supportedRatios: Readonly<Record<string, number>>;
  /** A direct List source image owns geometry over an inherited model default. */
  preferSourceAspectRatio?: boolean;
}

const positiveFinite = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value > 0
);

export const resolveNanoListAspectRatio = ({
  explicitAspectRatio,
  sourceWidth,
  sourceHeight,
  workspaceAspectRatio,
  rectAspectRatio,
  coversEntireFrame,
  supportedRatios,
  preferSourceAspectRatio = false,
}: ResolveNanoListAspectRatioInput): string => {
  const sourceRatio = positiveFinite(sourceWidth) && positiveFinite(sourceHeight)
    ? sourceWidth / sourceHeight
    : undefined;

  if (preferSourceAspectRatio && sourceRatio) {
    return closestSupportedAspectRatio(sourceRatio, supportedRatios);
  }

  if (
    typeof explicitAspectRatio === 'string'
    && explicitAspectRatio !== 'auto'
    && explicitAspectRatio !== 'match_input_image'
    && positiveFinite(supportedRatios[explicitAspectRatio])
  ) return explicitAspectRatio;
  const targetRatio = sourceRatio
    ?? (coversEntireFrame ? rectAspectRatio : workspaceAspectRatio ?? rectAspectRatio);

  let closest: { label: string | null; difference: number } = {
    label: null,
    difference: Number.POSITIVE_INFINITY,
  };
  for (const [label, ratio] of Object.entries(supportedRatios)) {
    const difference = Math.abs(ratio - targetRatio);
    if (difference < closest.difference) closest = { label, difference };
  }
  return closest.label && closest.difference < 0.002 ? closest.label : 'auto';
};

export const closestSupportedAspectRatio = (
  targetRatio: number,
  supportedRatios: Readonly<Record<string, number>>,
): string => {
  let closest: { label: string | null; difference: number } = {
    label: null,
    difference: Number.POSITIVE_INFINITY,
  };
  for (const [label, ratio] of Object.entries(supportedRatios)) {
    const difference = Math.abs(ratio - targetRatio);
    if (difference < closest.difference) closest = { label, difference };
  }
  return closest.label ?? 'auto';
};

export const assembleNanoListProviderParams = <T extends Record<string, unknown>>(
  params: T,
  ratioInput: ResolveNanoListAspectRatioInput,
): T & { aspect_ratio: string } => ({
  ...params,
  aspect_ratio: resolveNanoListAspectRatio(ratioInput),
});
